// components/CalendarToolbar.jsx
import React, { memo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Printer,
  RefreshCw,
  CalendarIcon,
  Grid,
  List,
  ChevronDown,
} from "../Icons";

const CalendarToolbar = memo(({
  locale,
  currentView,
  currentDate,
  isLoading,
  searchQuery,
  onPrev,
  onNext,
  onToday,
  onViewChange,
  onSearchChange,
  onRefresh,
  onPrint,
  onCreateBooking,
  onDateSelect,
  getWeekStart,
  addDays,
}) => {
  const { t } = useTranslation("global");
  const datePickerRef = useRef(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Format current date for the date picker input value
  const getFormattedDateForPicker = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, [currentDate]);

  // Handle date picker change
  const handleDatePickerChange = useCallback((e) => {
    if (onDateSelect && !isLoading) {
      const selectedDate = new Date(e.target.value);
      if (!isNaN(selectedDate.getTime())) {
        onDateSelect(selectedDate);
        setShowDatePicker(false);
      }
    }
  }, [onDateSelect, isLoading]);

  // Trigger native date picker when calendar icon is clicked
  const handleIconClick = useCallback(() => {
    if (datePickerRef.current && !isLoading) {
      datePickerRef.current.showPicker();
      setShowDatePicker(true);
    }
  }, [isLoading]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleIconClick();
    }
  }, [handleIconClick]);

  // Get formatted date range for week view
  const getWeekRange = useCallback(() => {
    const weekStart = getWeekStart(new Date(currentDate));
    const weekEnd = addDays(weekStart, 6);
    
    const startMonth = weekStart.toLocaleDateString(locale, { month: "short" });
    const endMonth = weekEnd.toLocaleDateString(locale, { month: "short" });
    const startDay = weekStart.getDate();
    const endDay = weekEnd.getDate();
    
    if (startMonth === endMonth) {
      return `${startMonth} ${startDay} - ${endDay}`;
    }
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
  }, [currentDate, locale, getWeekStart, addDays]);

  // Get formatted date for day view
  const getDayFormat = useCallback(() => {
    return currentDate.toLocaleDateString(locale, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, [currentDate, locale]);

  // Get formatted date for month view
  const getMonthFormat = useCallback(() => {
    return currentDate.toLocaleDateString(locale, {
      month: "long",
      year: "numeric",
    });
  }, [currentDate, locale]);

  // Get the appropriate date display text based on current view
  const getDateDisplayText = useCallback(() => {
    switch (currentView) {
      case "month":
        return getMonthFormat();
      case "week":
        return getWeekRange();
      case "day":
        return getDayFormat();
      default:
        return getMonthFormat();
    }
  }, [currentView, getMonthFormat, getWeekRange, getDayFormat]);

  // Handle search with debounce
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    onSearchChange(value);
  }, [onSearchChange]);

  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarLeft}>
        <div className={styles.logo}>
          <CalendarIcon size={24} aria-hidden="true" />
          <span>{t("calendar.title")}</span>
        </div>
        
        <div className={styles.actionButtons}>
          <button
            className={`${styles.iconButton} ${isLoading ? styles.spinning : ''}`}
            onClick={onRefresh}
            disabled={isLoading}
            aria-label={t("calendar.toolbar.refresh")}
            title={t("calendar.toolbar.refresh")}
          >
            <RefreshCw size={20} aria-hidden="true" />
          </button>
          
          <button
            className={styles.iconButton}
            onClick={onPrint}
            disabled={isLoading}
            aria-label={t("calendar.toolbar.print")}
            title={t("calendar.toolbar.print")}
          >
            <Printer size={20} aria-hidden="true" />
          </button>
          
          <button
            className={styles.createButton}
            onClick={onCreateBooking}
            disabled={isLoading}
            aria-label={t("calendar.timetable.addBooking")}
          >
            <Plus size={20} aria-hidden="true" />
            <span>{t("calendar.timetable.addBooking")}</span>
          </button>
        </div>
      </div>
      
      <div className={styles.toolbarRight}>
        <div className={styles.viewSwitcher} role="tablist" aria-label={t("calendar.toolbar.viewSelector")}>
          <button
            className={`${styles.viewButton} ${currentView === "month" ? styles.active : ''}`}
            onClick={() => onViewChange("month")}
            disabled={isLoading}
            role="tab"
            aria-selected={currentView === "month"}
            aria-label={t("calendar.timetable.month")}
          >
            <Grid size={16} aria-hidden="true" />
            <span>{t("calendar.timetable.month")}</span>
          </button>
          <button
            className={`${styles.viewButton} ${currentView === "week" ? styles.active : ''}`}
            onClick={() => onViewChange("week")}
            disabled={isLoading}
            role="tab"
            aria-selected={currentView === "week"}
            aria-label={t("calendar.timetable.week")}
          >
            <CalendarIcon size={16} aria-hidden="true" />
            <span>{t("calendar.timetable.week")}</span>
          </button>
          <button
            className={`${styles.viewButton} ${currentView === "day" ? styles.active : ''}`}
            onClick={() => onViewChange("day")}
            disabled={isLoading}
            role="tab"
            aria-selected={currentView === "day"}
            aria-label={t("calendar.timetable.day")}
          >
            <List size={16} aria-hidden="true" />
            <span>{t("calendar.timetable.day")}</span>
          </button>
        </div>
        
        <div className={styles.searchContainer}>
          <Search className={styles.searchIcon} size={18} aria-hidden="true" />
          <input
            type="text"
            className={styles.searchInput}
            placeholder={t("calendar.timetable.findBooking")}
            value={searchQuery}
            onChange={handleSearchChange}
            disabled={isLoading}
            aria-label={t("calendar.toolbar.search")}
          />
          {searchQuery && (
            <button
              className={styles.clearSearch}
              onClick={() => onSearchChange("")}
              aria-label={t("calendar.toolbar.clearSearch")}
            >
              ×
            </button>
          )}
        </div>
        
        <div className={styles.dateNavigation}>
          <button
            className={styles.navButton}
            onClick={onPrev}
            aria-label={t("calendar.toolbar.previous")}
            disabled={isLoading}
          >
            <ChevronLeft size={20} aria-hidden="true" />
          </button>
          
          <button
            className={styles.todayButton}
            onClick={onToday}
            disabled={isLoading}
            aria-label={t("calendar.timetable.today")}
          >
            {t("calendar.timetable.today")}
          </button>
          
          <button
            className={styles.navButton}
            onClick={onNext}
            aria-label={t("calendar.toolbar.next")}
            disabled={isLoading}
          >
            <ChevronRight size={20} aria-hidden="true" />
          </button>
          
          {/* Professional Date Selector */}
          <div className={styles.dateSelectorWrapper}>
            <button
              className={styles.dateSelectorIcon}
              onClick={handleIconClick}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              aria-label={t("calendar.toolbar.selectDate")}
              title={t("calendar.toolbar.selectDate")}
            >
              <CalendarIcon size={18} aria-hidden="true" />
              <ChevronDown size={12} className={styles.dropdownIcon} aria-hidden="true" />
            </button>
            <input
              ref={datePickerRef}
              type="date"
              className={styles.datePickerInput}
              value={getFormattedDateForPicker()}
              onChange={handleDatePickerChange}
              disabled={isLoading}
              aria-label={t("calendar.toolbar.datePicker")}
              min="1900-01-01"
              max="2100-12-31"
            />
          </div>
          
          <h2 className={styles.dateDisplay}>
            {getDateDisplayText()}
          </h2>
        </div>
      </div>
    </div>
  );
});

CalendarToolbar.displayName = "CalendarToolbar";

export default CalendarToolbar;